import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2, AlertCircle, Files, Pin } from 'lucide-react';
import { useUpload } from '../contexts/UploadContext';

// Floating status pill for the *bulk* upload flow. Reads the single
// active session from UploadContext and renders a compact progress
// summary at the bottom of the viewport when:
//   1. There IS an active session, AND
//   2. The current route is NOT the one that started the upload
//      (otherwise FileUploader is showing the full inline progress
//      card, which is a better view — no need to duplicate it).
//
// The floating bar is bulk-native: "N of M files · X.X MB/s · 47%"
// rather than the old single-filename pattern.

const humanBytes = (n: number): string => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

// Auto-hide the floating bar this long after phase flips to 'done'.
// Long enough that a user glancing at their monitor catches the
// "Uploaded N files" confirmation; short enough that a returning-from-
// tab-switch user isn't greeted by a stale success pill from an hour
// ago. Errors are NOT auto-hidden — the user needs to see the failure.
const AUTO_HIDE_MS = 8000;

const FloatingUploadBar: React.FC = () => {
  const { session, dismiss } = useUpload();
  const location = useLocation();
  // Local "the auto-timer decided to hide me" state, separate from
  // context dismiss(). Keeping this local means the underlying session
  // (and any inline card reading it on the origin page) stays intact
  // even after the floating bar hides itself. A manual X click still
  // calls dismiss() → kills the session globally, current behavior.
  const [autoHidden, setAutoHidden] = React.useState(false);

  // Reset the auto-hidden flag whenever a new session starts (identity
  // via session.id bump in UploadContext) so the next completion isn't
  // pre-hidden by the previous session's timer.
  React.useEffect(() => { setAutoHidden(false); }, [session?.id]);

  // Kick off the auto-hide timer as soon as phase transitions to 'done'.
  // Timer runs regardless of tab visibility (browsers throttle but don't
  // pause setTimeout on hidden tabs) so a user who was away comes back to
  // an already-cleared bar — matching every other iOS/Android toast in
  // the world.
  React.useEffect(() => {
    if (session?.phase !== 'done') return;
    const t = setTimeout(() => setAutoHidden(true), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [session?.phase, session?.id]);

  if (!session) return null;
  if (autoHidden) return null;
  // Note: previously hidden when on the page that started the upload
  // (originPath === current). That kept the UI clean but new users didn't
  // discover that the upload docks — they'd sit on the upload page
  // anxiously watching, not realising they could navigate away. Now the
  // bar appears immediately with a "docked" affordance to teach the
  // pattern; content shifts to compact/informational when on origin (the
  // inline FileUploader card is the primary progress view there) and to
  // full progress detail after navigation.
  const onOriginPage = session.originPath === location.pathname;

  const pct = session.progress
    ? Math.min(1, session.progress.bytesUploaded / Math.max(1, session.progress.totalBytes))
    : session.hashProgress
    ? session.hashProgress.hashed / Math.max(1, session.hashProgress.total)
    : 0;

  const status = (() => {
    // On the origin page, terminal states keep a short confirmation line.
    // Active phases (hashing/uploading) render a separate compact chip
    // below and never read `status` — the inline FileUploader card owns
    // the detailed progress there, so a second status line here would
    // just be noise.
    if (onOriginPage) {
      if (session.phase === 'done') {
        const p = session.progress;
        const done = p?.done ?? session.totalFiles;
        return `Docked · ${done} file${done === 1 ? '' : 's'} uploaded`;
      }
      if (session.phase === 'error') {
        return 'Docked · upload failed — see the card above';
      }
    }
    if (session.phase === 'hashing') {
      const hp = session.hashProgress;
      return hp
        ? `Checking for duplicates · ${hp.hashed}/${hp.total}`
        : 'Checking for duplicates…';
    }
    if (session.phase === 'uploading') {
      const p = session.progress;
      if (!p) return 'Uploading…';
      const elapsed = (Date.now() - session.startedAt) / 1000;
      const rate = elapsed > 0.5 ? `${humanBytes(p.bytesUploaded / elapsed)}/s` : '…';
      return `${p.done}/${p.total} files · ${humanBytes(p.bytesUploaded)}/${humanBytes(p.totalBytes)} · ${rate}`;
    }
    if (session.phase === 'done') {
      const p = session.progress;
      const done = p?.done ?? session.totalFiles;
      const skipped = p?.skipped ?? 0;
      return skipped > 0
        ? `Uploaded ${done - skipped} · ${skipped} already present`
        : `Uploaded ${done} file${done === 1 ? '' : 's'}`;
    }
    return session.error ?? 'Upload failed';
  })();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
      >
        <div className="mx-auto max-w-3xl px-4 pb-4 pointer-events-auto">
          <div className={`relative rounded-2xl bg-space-900/85 border backdrop-blur-md shadow-2xl overflow-hidden transition-opacity ${
            onOriginPage ? 'border-white/[0.08] opacity-80' : 'border-white/10'
          }`}>
            {/* Progress ribbon — hidden on origin page because the inline
                FileUploader card is showing the same ribbon at higher
                fidelity. Off-origin we show it here as the primary read. */}
            {!onOriginPage && (session.phase === 'uploading' || session.phase === 'hashing') && (
              <div className="h-1 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-nebula-400 to-aurora-400 transition-all"
                  style={{ width: `${Math.max(2, pct * 100)}%` }}
                />
              </div>
            )}
            {!onOriginPage && session.phase === 'done' && <div className="h-1 bg-emerald-500/60" />}
            {!onOriginPage && session.phase === 'error' && <div className="h-1 bg-red-500/70" />}

            {/* On-origin during an active phase, the pill exists purely to
                teach "this docks — you can navigate away". Mimicking the
                inline card's spinner + title + status makes it read as a
                duplicated progress card, so we collapse to a single-line
                static chip with a Pin. Terminal states keep the fuller
                layout since they double as a confirmation banner. */}
            {onOriginPage && (session.phase === 'hashing' || session.phase === 'uploading') ? (
              <div className="flex items-center gap-3 px-5 py-2.5">
                <Pin className="h-4 w-4 text-white/40 shrink-0" />
                <div className="text-xs text-white/60 truncate">
                  Docked — you can navigate away, this will follow you
                </div>
              </div>
            ) : (
              <div className={`flex items-center gap-4 px-5 ${onOriginPage ? 'py-3' : 'py-4'}`}>
                <div className="shrink-0">
                  {session.phase === 'hashing' || session.phase === 'uploading' ? (
                    <Loader2 className="h-5 w-5 text-nebula-300 animate-spin" />
                  ) : session.phase === 'done' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-300" />
                  )}
                </div>

                <Files className="h-5 w-5 text-white/40 shrink-0 hidden md:block" />

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white/90 truncate">
                    {onOriginPage ? (
                      // Already on this collection's page — link would no-op,
                      // so just render the name plain.
                      <span>{session.collectionId}</span>
                    ) : (
                      <Link
                        to={session.originPath}
                        className="hover:text-white transition-colors"
                        title="Back to upload page"
                      >
                        {session.collectionId}
                      </Link>
                    )}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5 truncate">{status}</div>
                </div>

                {(session.phase === 'done' || session.phase === 'error') && (
                  <button
                    onClick={dismiss}
                    className="shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Dismiss"
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingUploadBar;
